import React, { useState, useEffect } from 'react';
import DataTable from '../../../components/ui/DataTable';
import { budgetQuotationService } from '../../budget/services/budgetQuotationService';
import { Eye, Download, Check, X } from 'lucide-react';
import { Modal } from '../../../components/ui';
import { Button } from '../../../components/ui/Button';
import toast from 'react-hot-toast';
import { downloadPdfFromUrl } from '../../ordering/utils/downloadPdf';
import { PdfViewer } from '../../ordering/components/pdfviewer';
import { API_BASE_URL } from '../../../services/api';

export const BudgetApprovalsTable = ({ search = "", filters = {} }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Approval modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // PDF View state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedPdfDoc, setSelectedPdfDoc] = useState(null);

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const res = await budgetQuotationService.getQuotations();
      if (res.success && res.data) {
        // Show only those submitted for approval
        const filteredData = res.data.filter(q => 
          q.status !== 'Draft'
        );
        setData(filteredData);
      }
    } catch (error) {
      console.error("Failed to fetch quotations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotations();
  }, []);

  const filteredData = React.useMemo(() => {
    let result = [...data];

    if (search) {
      const term = search.toLowerCase();
      result = result.filter(q =>
        (q.projectName || '').toLowerCase().includes(term) ||
        (q.quotationNo || '').toLowerCase().includes(term) ||
        (q.site || '').toLowerCase().includes(term) ||
        (q.referenceDesigner || '').toLowerCase().includes(term)
      );
    }

    const { date, fromDate, toDate, status } = filters;

    if (status && status !== "all") {
      result = result.filter(q => String(q.status || "Pending").toLowerCase() === String(status).toLowerCase());
    }

    if (date !== "all" || fromDate || toDate) {
      let startDate = null;
      let endDate = null;

      const now = new Date();
      if (date && date !== "all") {
        startDate = new Date();
        switch (date) {
          case "today": startDate.setHours(0, 0, 0, 0); break;
          case "this_week": case "week": startDate.setDate(now.getDate() - 7); break;
          case "this_month": case "month": startDate.setDate(now.getDate() - 30); break;
          case "this_year": case "year": startDate.setDate(now.getDate() - 365); break;
        }
        endDate = now;
      } else {
        if (fromDate) startDate = new Date(fromDate);
        if (toDate) endDate = new Date(toDate);
      }

      if (startDate || endDate) {
        result = result.filter((q) => {
          const createdAt = new Date(q.date || q.createdAt);
          if (startDate && createdAt < startDate) return false;
          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (createdAt > end) return false;
          }
          return true;
        });
      }
    }

    return result;
  }, [data, search, filters]);

  const handleActionClick = (quotation, type) => {
    setSelectedQuotation(quotation);
    setActionType(type);
    setIsModalOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedQuotation) return;
    
    setActionLoading(true);
    try {
      const newStatus = actionType === 'approve' ? 'Approved' : 'Rejected';
      const res = await budgetQuotationService.updateStatus(selectedQuotation._id, newStatus);
      if (res.success) {
        toast.success(`Budget ${newStatus.toLowerCase()} successfully`);
        setIsModalOpen(false);
        fetchQuotations();
      } else {
        toast.error(`Failed to ${actionType} budget`);
      }
    } catch (err) {
      toast.error(`Error processing request`);
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewPdf = (row) => {
    if (row.pdfUrl) {
      const token = localStorage.getItem('blum_token') || localStorage.getItem('token') || '';
      const rawUrl = row.pdfUrl.startsWith('http') ? row.pdfUrl : `${API_BASE_URL}${row.pdfUrl}`;
      const separator = rawUrl.includes('?') ? '&' : '?';
      const fileUrlWithToken = token ? `${rawUrl}${separator}token=${token}` : rawUrl;

      setSelectedPdfDoc({
        fileUrl: fileUrlWithToken,
        downloadUrl: rawUrl,
        title: `BudgetQuotation_${row.quotationNo || 'VK'}.pdf`
      });
      setIsViewModalOpen(true);
    }
  };

  const columns = [
    { header: 'Project Name', accessor: 'projectName' },
    { header: 'Quotation No', accessor: 'quotationNo' },
    { header: 'Site', accessor: 'site' },
    { header: 'Designer', accessor: 'referenceDesigner' },
    { header: 'Created Date', accessor: 'date' },
    { 
      header: 'Grand Total', 
      accessor: (row) => row.totals?.grandTotal 
        ? (row.totals.grandTotal.toString().startsWith('₹') ? row.totals.grandTotal : `₹${Number(row.totals.grandTotal).toLocaleString('en-IN')}`)
        : '-' 
    },
    {
      header: 'Status',
      accessor: (row) => (
        <span className={`px-2.5 py-1 border rounded-md text-[11px] font-semibold ${
          row.status === 'Approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
          row.status === 'Rejected' ? 'bg-red-50 text-red-700 border-red-200' :
          row.status === 'Draft' ? 'bg-slate-50 text-slate-600 border-slate-200' :
          'bg-amber-50 text-amber-700 border-amber-200'
        }`}>
          {row.status || 'Pending'}
        </span>
      )
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <div className="flex items-center gap-3">
          {row.pdfUrl && (
            <button 
              onClick={() => handleViewPdf(row)} 
              className="text-slate-400 hover:text-brand-blue transition-colors" 
              title="View PDF"
            >
              <Eye size={18} />
            </button>
          )}

          {row.status === 'Pending' && (
            <>
              <button 
                onClick={() => handleActionClick(row, 'approve')} 
                className="text-emerald-500 hover:text-emerald-600 bg-emerald-50 hover:bg-emerald-100 p-1.5 rounded transition-colors" 
                title="Approve"
              >
                <Check size={16} />
              </button>
              <button 
                onClick={() => handleActionClick(row, 'reject')} 
                className="text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100 p-1.5 rounded transition-colors" 
                title="Reject"
              >
                <X size={16} />
              </button>
            </>
          )}

          {row.status === 'Approved' && row.pdfUrl && (
            <button onClick={() => downloadPdfFromUrl(row.pdfUrl, `BudgetQuotation_${row.quotationNo || 'VK'}.pdf`)} className="text-slate-400 hover:text-brand-blue transition-colors" title="Download PDF">
              <Download size={18} />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <DataTable 
          columns={columns} 
          data={filteredData}
          isLoading={loading}
          loadingText="Loading budget approvals..."
          emptyMessage="No budget quotations found."
        />
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => !actionLoading && setIsModalOpen(false)}
        title={`${actionType === 'approve' ? 'Approve' : 'Reject'} Budget Quotation`}
        size="md"
      >
        <div className="p-6">
          <p className="text-slate-600 mb-6">
            Are you sure you want to {actionType} the budget quotation for <span className="font-semibold text-slate-800">{selectedQuotation?.projectName}</span>? 
            {actionType === 'approve' ? ' This will mark it as approved and allow further processing.' : ' This will mark it as rejected.'}
          </p>
          <div className="flex justify-end gap-3">
            <Button 
              variant="secondary" 
              onClick={() => setIsModalOpen(false)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button 
              variant={actionType === 'approve' ? 'primary' : 'danger'}
              onClick={handleConfirmAction}
              disabled={actionLoading}
            >
              {actionLoading ? 'Processing...' : `Confirm ${actionType === 'approve' ? 'Approval' : 'Rejection'}`}
            </Button>
          </div>
        </div>
      </Modal>

      {/* PDF Viewer Modal */}
      {isViewModalOpen && selectedPdfDoc && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-100 text-red-600 flex items-center justify-center">
                  <Eye size={18} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">{selectedPdfDoc.title}</h3>
                  <p className="text-xs text-slate-500">Document Preview</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={() => downloadPdfFromUrl(selectedPdfDoc.downloadUrl || selectedPdfDoc.fileUrl, selectedPdfDoc.title)}
                  className="gap-2"
                >
                  <Download size={16} />
                  Download
                </Button>
                <div className="w-px h-6 bg-slate-300 mx-1"></div>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className="flex-1 bg-slate-100 relative overflow-hidden">
              <iframe
                src={`${selectedPdfDoc.fileUrl}#view=FitH`}
                className="w-full h-full border-0"
                title={selectedPdfDoc.title}
                onError={(e) => {
                  console.error('PDF iframe error:', e);
                  toast.error("Failed to load PDF preview");
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
